import { ExtendedIPdfConfig, pdf } from "./pdf";
import { Config, ExtendedConfig, IBaseConfig, IPdfConfig } from "./types";
import Browser from "./utils/browser";
import { cleanupFast } from "./utils/cleanUp";

const printTypes = ["pdf", "html", "image", "json", "raw-html"];

// Define default configs
const baseConfig: IBaseConfig = {
  type: "pdf",
  frameId: "printTS",
  iframeRemovalDelay: 0,
  onError: (error: string) => {
    throw new Error(error);
  }
};

const baseIPdfConfig: Partial<IPdfConfig> = {
  base64: false
};

// Main initializer
function printTS(config: Config) {
  const printConfig = {
    ...baseConfig,
    ...config
  };

  // Process parameters
  validateConfig(printConfig);

  // Start
  start(printConfig);
}

function validateConfig({ type, printable }: ExtendedConfig) {
  // Validate printable
  if (!printable) {
    throw new Error("Missing printable information.");
  }

  // Validate type
  if (typeof type !== "string" || !printTypes.includes(type.toLowerCase())) {
    throw new Error("Invalid print type. Available types are: pdf");
  }
}

function start(config: ExtendedConfig) {
  const { onLoadingStart, frameId } = config;

  // Check for a print start hook function
  onLoadingStart?.();

  // To prevent duplication and issues, remove any used printFrame from the DOM
  clearPrintFrames(frameId);

  // Create a new iframe for the print job
  const printFrame = getPrintFrame(config);

  startPdf({ ...baseIPdfConfig, ...config }, printFrame);
}

function createBlobAndPrint(data: Uint8Array) {
  // Pass response or base64 data to a blob and create a local object url
  const localPdfBlb = new window.Blob([data], { type: "application/pdf" });
  return window.URL.createObjectURL(localPdfBlb);
}

function getBlob(printable: string) {
  return Uint8Array.from(window.atob(printable as string), (c) => c.charCodeAt(0));
}

function getFallbackPrintable({ printable, fallbackPrintable, base64 }: IPdfConfig) {
  if (fallbackPrintable) {
    return base64 ? createBlobAndPrint(getBlob(fallbackPrintable)) : fallbackPrintable;
  }

  return base64 ? createBlobAndPrint(getBlob(printable)) : printable;
}

function printFallback(config: ExtendedIPdfConfig, hasHref?: boolean) {
  const fallbackPrintable = getFallbackPrintable(config);
  if (hasHref) {
    window.location.href = fallbackPrintable;
    return;
  }
  const win = window.open(fallbackPrintable, "_blank", "toolbar=yes,scrollbars=yes,menubar=no,location=no,resizable=yes");
  win?.focus();
}

function startPdf(config: ExtendedIPdfConfig, printFrame: HTMLIFrameElement) {
  const { onLoadingEnd, onIncompatibleBrowser, onError } = config;

  // Check browser support for pdf and if not supported we will just open the pdf file instead
  if (Browser.isIE || Browser.isChromeMobile || Browser.isIosChrome) {
    try {
      const hasHref = !!Browser.isChromeMobile || config.base64;
      printFallback(config, hasHref);
      cleanupFast(config);
      onIncompatibleBrowser?.();
    } catch (error: any) {
      onError?.(error);
    } finally {
      onLoadingEnd?.();
    }
    return;
  }
  pdf(config, printFrame);
}

function clearPrintFrames(frameId: string) {
  const usedFrame = document.getElementById(frameId);
  if (!usedFrame) {
    return;
  }
  usedFrame.parentNode?.removeChild(usedFrame);
}

function getPrintFrame({ frameId }: ExtendedConfig) {
  // Create a new iframe for the print job
  const printFrame = document.createElement("iframe");

  // Set iframe element id
  printFrame.setAttribute("id", frameId);

  if (Browser.isFirefox) {
    // Set the iframe to be is visible on the page (guaranteed by fixed position) but hidden using opacity 0, because
    // this works in Firefox. The height needs to be sufficient for some part of the document other than the PDF
    // viewer's toolbar to be visible in the page
    printFrame.setAttribute(
      "style",
      "width: 1px; height: 100px; position: fixed; left: 0; top: 0; opacity: 0; border-width: 0; margin: 0; padding: 0"
    );
  } else {
    // Hide the iframe in other browsers
    printFrame.setAttribute("style", "visibility: hidden; height: 0; width: 0; position: absolute; border: 0");
  }

  return printFrame;
}
export { printTS };
